import { Box, Checkbox, Flex, Text } from "@chakra-ui/react";
import { Col } from "antd";
import Field from "components/Input/Field";
import SelectBooleano from "components/Input/SelectBooleano";
import SelectContaBancaria from "components/Input/SelectContaBancaria";
import SelectFormaPagamento from "components/Input/SelectFormaPagamento";
import SelectNumber from "components/Input/SelectNumber";
import SelectPessoas from "components/Input/SelectPessoas";
import SelectTipoIntervaloRepeticao from "components/Input/SelectTipoIntervaloRepeticao/index";
import { TipoIntervaloRepeticaoEnum } from "core/features/manutencao/typings";
import { ContaRecorrenteForm } from "features/conta-recorrente/typings";
import { useState } from "react";
import { Control, UseFormSetValue, UseFormGetValues } from "react-hook-form";
import { OperacaoEnum } from "resources/enums/conta";
import ComposicaoContaLista from "./components/ComposicaoContaLista";
import { InputBaseWrapper } from "./components/InputBaseWrapper";
import ModalComposicaoConta from "./components/ModalComposicaoConta";
import SelectBase from "./components/SelectBase";
import { mapTipoEntidadeToLabel, useOptionsTipoEntidade } from "./util/helper";
import SelectMes from "components/Input/SelectMes";

interface FormContaRecorrenteProps {
  operacao: OperacaoEnum;
  form: ContaRecorrenteForm;
  setValue: UseFormSetValue<ContaRecorrenteForm>;
  control: Control<ContaRecorrenteForm>;
  getValues: UseFormGetValues<ContaRecorrenteForm>;
}

function FormContaRecorrente({
  operacao,
  form,
  setValue,
  control,
  getValues,
}: FormContaRecorrenteProps) {
  const [isOpenComposicaoConta, setOpenComposicaoConta] = useState(false);

  const options = useOptionsTipoEntidade();

  return (
    <Flex flexDir="column">
      <InputBaseWrapper gutter={16}>
        <Box as={Col} xs={24}>
          <Field
            control={control}
            rules={{ required: true }}
            name="descricao"
            label="Descrição"
          />
        </Box>
      </InputBaseWrapper>

      <InputBaseWrapper gutter={16}>
        <Box as={Col} xs={12}>
          <Field
            control={control}
            name="tipoEntidadeId"
            label="Entidade"
            rules={{
              required: true,
            }}
            render={({ field, isInvalid }) => (
              <SelectBase
                isInvalid={isInvalid}
                onChange={(ev) => {
                  field.onChange(ev);
                  setValue("pessoaId", "");
                }}
                value={field.value}
                options={options}
              />
            )}
          />
        </Box>

        <Box as={Col} xs={12}>
          <Field
            control={control}
            name="pessoaId"
            label={mapTipoEntidadeToLabel(form.tipoEntidadeId)}
            rules={{
              required: true,
            }}
            render={({ field, isInvalid }) => (
              <SelectPessoas
                isInvalid={isInvalid}
                onChange={field.onChange}
                value={field.value}
                tipoEntidades={form.tipoEntidadeId ? [form.tipoEntidadeId] : []}
              />
            )}
          />
        </Box>
      </InputBaseWrapper>

      <InputBaseWrapper gutter={16}>
        {operacao === OperacaoEnum.Receita ? (
          <Box as={Col} xs={12}>
            <Field
              control={control}
              name="formasRecebimentoIds"
              label="Métodos de cobrança"
              shouldUnregister
              rules={{
                required: true,
              }}
              render={({ field, isInvalid }) => (
                <SelectFormaPagamento
                  queryParams={{
                    aceitaRecebimentoOnline: true,
                    ativo: true,
                  }}
                  isInvalid={isInvalid}
                  onChange={field.onChange}
                  value={field.value}
                  mode="multiple"
                  placeholder="Selecione..."
                />
              )}
            />
          </Box>
        ) : null}

        <Box as={Col} xs={12}>
          <Field
            shouldUnregister
            control={control}
            name="tenantContaId"
            label={
              operacao === OperacaoEnum.Despesa
                ? "Conta de Pagamento"
                : "Conta de Recebimento"
            }
            rules={{
              required: true,
            }}
            render={({ field, isInvalid }) => (
              <SelectContaBancaria
                isInvalid={isInvalid}
                onChange={field.onChange}
                value={field.value}
                markerDefault
                incluirInativas={false}
              />
            )}
          />
        </Box>
      </InputBaseWrapper>

      {operacao === OperacaoEnum.Receita ? (
        <InputBaseWrapper gutter={16}>
          <Box as={Col} xs={8}>
            <Field
              control={control}
              name="calcularJurosAutomatico"
              render={({ field }) => (
                <Checkbox
                  onChange={() => field.onChange(!field.value)}
                  isChecked={!!field.value}
                >
                  <Text fontSize={12}>Calcular juros após atraso?</Text>
                </Checkbox>
              )}
            />
          </Box>
        </InputBaseWrapper>
      ) : null}

      <InputBaseWrapper gutter={[16, 16]}>
        <Box as={Col} xs={6}>
          <Field
            control={control}
            name="tipoIntervaloRepeticaoId"
            label="Intervalo"
            rules={{
              required: true,
            }}
            render={({ field, isInvalid }) => (
              <SelectTipoIntervaloRepeticao
                {...field}
                isInvalid={isInvalid}
                hideOptions={["diaria", "semanal"]}
              />
            )}
          />
        </Box>

        {form.tipoIntervaloRepeticaoId === TipoIntervaloRepeticaoEnum.Anual ? (
          <Box as={Col} xs={6}>
            <Field
              control={control}
              name="mes"
              label="Mês fixo"
              shouldUnregister
              rules={{
                required: true,
              }}
              render={({ field, isInvalid }) => (
                <SelectMes {...field} useNumericValue />
              )}
            />
          </Box>
        ) : null}

        <Box as={Col} xs={6}>
          <Field
            control={control}
            name="dia"
            label="Dia fixo"
            shouldUnregister
            rules={{
              required: true,
            }}
            render={({ field, isInvalid }) => (
              <SelectNumber {...field} min={1} max={31} />
            )}
          />
        </Box>

        <Box as={Col} xs={6}>
          <Field
            control={control}
            name="dataInicio"
            label="Data de início"
            rules={{
              required: true,
            }}
            inputProps={{
              type: "date",
            }}
          />
        </Box>

        <Box as={Col} xs={6}>
          <Field
            control={control}
            name="dataFim"
            label="Data de fim"
            inputProps={{
              type: "date",
            }}
          />
        </Box>

        <Box as={Col} xs={6}>
          <Field
            control={control}
            name="baixaAutomatica"
            label="Baixa automática"
            rules={{
              required: true,
            }}
            render={({ field, isInvalid }) => <SelectBooleano {...field} />}
          />
        </Box>

        <Box as={Col} xs={6}>
          <Field
            control={control}
            name="gerarEmDiaUtil"
            label="Gerar em dia útil"
            rules={{
              required: true,
            }}
            render={({ field, isInvalid }) => <SelectBooleano {...field} />}
          />
        </Box>

        <Box as={Col} xs={6}>
          <Field
            control={control}
            name="gerarContaAutomatico"
            label="Lançamento automático?"
            render={({ field, isInvalid }) => <SelectBooleano {...field} />}
          />
        </Box>

        {form.gerarContaAutomatico === "s" ? (
          <Box as={Col} xs={8}>
            <Field
              control={control}
              name="diaGerarAutomatico"
              label="Dia p/ lançamento automático"
              shouldUnregister
              rules={{
                required: true,
              }}
              render={({ field, isInvalid }) => (
                <SelectNumber {...field} min={1} max={31} />
              )}
            />
          </Box>
        ) : null}
      </InputBaseWrapper>

      <ComposicaoContaLista
        operacao={operacao}
        form={form}
        control={control}
        onAdd={() => setOpenComposicaoConta(true)}
        getValues={getValues}
        showComplemento={false}
      />

      {isOpenComposicaoConta ? (
        <ModalComposicaoConta
          operacao={operacao}
          isOpen={isOpenComposicaoConta}
          onClose={() => setOpenComposicaoConta(false)}
          onSubmit={(item) => {
            const items = form.composicoes;

            const newItems = items.concat(item);

            setValue("composicoes", newItems);

            setOpenComposicaoConta(false);
          }}
        />
      ) : null}
    </Flex>
  );
}

export default FormContaRecorrente;
